import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import {
  PensioenBijEventsQueryKey,
  usePensioenbijUnSyncEventsGetAllQuery,
  ConnectionPensioenbijMutationsQueryKey,
  useConnectionPensioenbijMutationsQuery,
  EmployeePensioenbijMutationsQueryKey,
  useEmployeePensioenbijMutationsQuery,
  useResendPensioenbijMutation,
  useDeletePensioenbijMutation,
  type PensioenbijMutation,
} from '../../../../hooks/api/tools';
import { StoreSelectors, useStoreSelector } from '../../../../store';
import { useOwnerGetEmployersQuery, useSnakeBar } from '../../../../hooks';
import type { Employer } from '../../../../types';
import EventsToolbar from 'app/Domain/Tools/components/PensioenbijEventTableActions/EventsToolbar';
import { useQueryClient } from '@tanstack/react-query';
import { PENSIOENBIJ_STATUS_ALL } from '../../../Tools/Ducks';
import { PensioenbijMutationDetailModal } from './PensioenbijMutationDetailModal';
import {
  DASHBOARD_PENSIOENBIJ_COLUMNS,
  PensioenbijMutationsTable,
  type PensioenbijTableColumn,
} from './PensioenbijMutationsTable';

const PENSIOENBIJ_DELETABLE_STATUSES = new Set(['pending', 'failed']);

type PensioenbijFilters = {
  status: string;
  employer: string | null;
  mutationType: string | null;
};

type PensioenbijPageProps = {
  hideEmployerFilter?: boolean;
  connectionId?: string;
  employeeId?: string;
  employerId?: string;
  employerContextId?: string;
  tableColumns?: PensioenbijTableColumn[];
};

export const PensioenbijPage = ({
  hideEmployerFilter = false,
  connectionId,
  employeeId,
  employerId,
  employerContextId,
  tableColumns = DASHBOARD_PENSIOENBIJ_COLUMNS,
}: PensioenbijPageProps) => {
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const queryClient = useQueryClient();
  const { mutate: resendSelectedMutations } = useResendPensioenbijMutation();
  const { mutate: deleteSelectedMutations, isLoading: isDeleting } = useDeletePensioenbijMutation();
  const userId = useStoreSelector<string>(state => StoreSelectors.AppSelector.selectUserId(state.AppReducer));

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<PensioenbijFilters>({
    status: PENSIOENBIJ_STATUS_ALL,
    employer: null,
    mutationType: null,
  });
  const [selectedMutation, setSelectedMutation] = useState<PensioenbijMutation | null>(null);

  const { data: employersData } = useOwnerGetEmployersQuery({
    variables: { userId: userId || '', page: 1, perPage: 500 },
    options: { enabled: !!userId && !hideEmployerFilter },
  });

  const employerOptions = React.useMemo(
    () => (employersData?.data ?? []).map((e: Employer) => ({ value: e.employerId, label: e.employerName })),
    [employersData?.data],
  );

  const statusFilter = filters.status !== PENSIOENBIJ_STATUS_ALL ? [filters.status] : undefined;

  const { data: globalEvents, isLoading: isLoadingGlobal } = usePensioenbijUnSyncEventsGetAllQuery({
    options: { enabled: !connectionId && !employeeId },
    variables: {
      page,
      pageSize,
      status: statusFilter,
      searchQuery,
      employerId: employerContextId ?? filters.employer ?? undefined,
      mutationType: filters.mutationType ?? undefined,
    },
  });

  const { data: connectionEvents, isLoading: isLoadingConnection } = useConnectionPensioenbijMutationsQuery({
    options: { enabled: !!connectionId },
    variables: {
      connectionId: connectionId ?? '',
      page,
      pageSize,
      status: statusFilter,
      searchQuery,
      mutationType: filters.mutationType ?? undefined,
    },
  });

  const { data: employeeEvents, isLoading: isLoadingEmployee } = useEmployeePensioenbijMutationsQuery({
    options: { enabled: !!employeeId && !!employerId },
    variables: {
      employeeId: employeeId ?? '',
      employerId: employerId ?? '',
      page,
      pageSize,
      status: statusFilter,
      searchQuery,
      mutationType: filters.mutationType ?? undefined,
    },
  });

  let events = globalEvents;
  let isLoading = isLoadingGlobal;

  if (connectionId) {
    events = connectionEvents;
    isLoading = isLoadingConnection;
  } else if (employeeId) {
    events = employeeEvents;
    isLoading = isLoadingEmployee;
  }

  const pensioenbijEvents = React.useMemo<PensioenbijMutation[]>(() => {
    if (isLoading) return [];
    return events?.data ?? [];
  }, [events?.data, isLoading]);

  const isMutationDeletable = useCallback(
    (mutation: PensioenbijMutation) => PENSIOENBIJ_DELETABLE_STATUSES.has(mutation.syncStatus),
    [],
  );

  const onToggleRow = useCallback((id: string) => {
    setSelectedRows(prev => (prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]));
  }, []);

  const onSelectAllChange = useCallback(() => {
    const deletableEvents = (events?.data ?? []).filter(isMutationDeletable);
    const areAllDeletableSelected =
      deletableEvents.length > 0 && deletableEvents.every(m => selectedRows.includes(m.externalId));
    setSelectedRows(areAllDeletableSelected ? [] : deletableEvents.map(m => m.externalId));
  }, [events, selectedRows, isMutationDeletable]);

  const invalidateAllQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [PensioenBijEventsQueryKey] });
    queryClient.invalidateQueries({ queryKey: [ConnectionPensioenbijMutationsQueryKey] });
    queryClient.invalidateQueries({ queryKey: [EmployeePensioenbijMutationsQueryKey] });
  }, [queryClient]);

  const onResendSelectedEvents = useCallback(() => {
    if (selectedRows.length === 0) return;
    const method = 'resendSelectedEvents';
    resendSelectedMutations(
      { mutationIds: selectedRows, connectionId: '01939b86-e07d-7bb5-b409-89a3e2e1c672' },
      {
        onSuccess: () => {
          showSuccessSnakeBar({ method });
          invalidateAllQueries();
          setSelectedRows([]);
        },
        onError: (error: any) => showErrorSnakeBar({ method, message: error.message }),
      },
    );
  }, [resendSelectedMutations, selectedRows, showSuccessSnakeBar, showErrorSnakeBar, invalidateAllQueries]);

  const onResendAllEvents = useCallback(() => {
    setSelectedRows([]);
    onResendSelectedEvents();
  }, [onResendSelectedEvents]);

  const onDeleteSelectedEvents = useCallback(() => {
    if (selectedRows.length === 0) return;
    const method = 'deleteSelectedMutations';
    deleteSelectedMutations(
      { mutationIds: selectedRows },
      {
        onSuccess: () => {
          showSuccessSnakeBar({ method });
          invalidateAllQueries();
          setSelectedRows([]);
        },
        onError: (error: any) => showErrorSnakeBar({ method, message: error.message }),
      },
    );
  }, [deleteSelectedMutations, selectedRows, showSuccessSnakeBar, showErrorSnakeBar, invalidateAllQueries]);

  const onPaginationChange = useCallback(({ rowSize, page: newPage }: { rowSize?: number; page?: number }) => {
    if (rowSize) setPageSize(rowSize);
    if (newPage) setPage(newPage);
  }, []);

  const applyFilter = useCallback((filter: { name: string; value: any }) => {
    setFilters(prev => ({ ...prev, [filter.name]: filter.value }));
    setPage(1);
  }, []);

  const onSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const pagination = React.useMemo(
    () => ({ page, pageSize, totalCount: events?.totalItems }),
    [page, pageSize, events?.totalItems],
  );

  return (
    <Box marginTop={1}>
      <EventsToolbar
        hasSelectedItems={selectedRows.length > 0}
        selectedStatus={filters.status}
        selectedEmployer={filters.employer}
        selectedMutationType={filters.mutationType}
        employerOptions={employerOptions}
        hideEmployerFilter={hideEmployerFilter}
        onChangeFilter={applyFilter}
        onSearch={onSearch}
        processEvents={onResendSelectedEvents}
        processAllEvents={onResendAllEvents}
        deleteSelectedEvents={onDeleteSelectedEvents}
      />
      <PensioenbijMutationsTable
        columns={tableColumns}
        rows={pensioenbijEvents}
        selectedRowsIds={selectedRows}
        onToggleRow={onToggleRow}
        onSelectAllChange={onSelectAllChange}
        onOpenMutationDetail={setSelectedMutation}
        loading={isLoading || isDeleting}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        isMutationDeletable={isMutationDeletable}
      />
      <PensioenbijMutationDetailModal mutation={selectedMutation} onClose={() => setSelectedMutation(null)} />
    </Box>
  );
};
